/**
 * Single emit point for "a render job the user started has FAILED".
 *
 * A failure can reach the client by several paths: the WS `renders/failed`
 * push, or any reconcile layer that finds the job terminal-failed on the API
 * (L1 API refresh, L2 WS reconnect, L3 tab visible, L4 watchdog incl. a 404
 * on a job the user started). Whichever path sees it first surfaces ONE toast;
 * the rest are deduped by render id. Without this, a failure healed by
 * reconcile (WS gap) flipped the card silently and the user never learned why.
 *
 * The message is the server's text (`error_message` on the render resource,
 * `error` on the WS payload). `null` → the presenter shows the localized
 * generic "Generation failed" string. The presenter is registered by a React
 * hook (`useRenderFailureToasts`) because the fallback needs translations.
 */
type RenderFailurePresenter = (message: string | null) => void;

let presenter: RenderFailurePresenter | null = null;
const notifiedRenderIds = new Set<string>();

export function registerRenderFailurePresenter(
  next: RenderFailurePresenter
): () => void {
  presenter = next;
  return () => {
    if (presenter === next) presenter = null;
  };
}

export function notifyRenderFailed(
  renderId: string,
  message: string | null | undefined
): void {
  if (notifiedRenderIds.has(renderId)) return;
  notifiedRenderIds.add(renderId);
  const text = message?.trim();
  presenter?.(text ? text : null);
}
